import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { selectClass } from './common';

const Field = ({ label, hint, children }) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

const Shell = ({ open, onOpenChange, title, description, submitLabel, onSubmit, submitting, children, wide = false, destructive = false, testId }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className={`${wide ? 'max-w-lg' : 'max-w-md'} max-h-[92vh] overflow-y-auto`} data-testid={testId}>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-4 py-4">{children}</div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>İptal</Button>
          <Button type="submit" variant={destructive ? 'destructive' : 'default'} disabled={submitting} data-testid={`${testId}-submit`}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{submitLabel}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export const ResetPasswordDialog = ({ open, onOpenChange, user, value, onChange, onSubmit, submitting }) => (
  <Shell open={open} onOpenChange={onOpenChange} title="Şifre sıfırla" description={user ? `${user.email} için yeni webmail giriş şifresi.` : ''} submitLabel="Şifreyi sıfırla" onSubmit={onSubmit} submitting={submitting} testId="reset-dialog">
    <Field label="Yeni şifre"><Input type="password" value={value} onChange={(e) => onChange(e.target.value)} placeholder="En az 6 karakter" autoFocus autoComplete="new-password" /></Field>
  </Shell>
);

export const AddIpDialog = ({ open, onOpenChange, title, description, value, onChange, note, onNote, onSubmit, submitting, testId = 'add-ip-dialog' }) => (
  <Shell open={open} onOpenChange={onOpenChange} title={title} description={description} submitLabel="Ekle" onSubmit={onSubmit} submitting={submitting} testId={testId}>
    <Field label="IP adresi"><Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="192.168.1.1" autoFocus /></Field>
    {onNote && <Field label="Açıklama (opsiyonel)"><Input value={note} onChange={(e) => onNote(e.target.value)} placeholder="Örn: Ofis IP'si" /></Field>}
  </Shell>
);

export const EditUserDialog = ({ open, onOpenChange, form, setForm, onSubmit, submitting }) => (
  <Shell open={open} onOpenChange={onOpenChange} title="Kullanıcıyı düzenle" submitLabel="Kaydet" onSubmit={onSubmit} submitting={submitting} wide testId="edit-dialog">
    <Field label="Görünen ad"><Input value={form.display_name || ''} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></Field>
    <div className="grid grid-cols-3 gap-3">
      <div className="col-span-2"><Field label="IMAP sunucu"><Input value={form.imap_host || ''} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} /></Field></div>
      <Field label="Port"><Input type="number" value={form.imap_port ?? ''} onChange={(e) => setForm({ ...form, imap_port: parseInt(e.target.value) || '' })} /></Field>
      <div className="col-span-2"><Field label="SMTP sunucu"><Input value={form.smtp_host || ''} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} /></Field></div>
      <Field label="Port"><Input type="number" value={form.smtp_port ?? ''} onChange={(e) => setForm({ ...form, smtp_port: parseInt(e.target.value) || '' })} /></Field>
    </div>
    <div className="pt-3 border-t border-border space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div><Label>Toplu mail özelliği</Label><p className="text-xs text-muted-foreground">Kullanıcı için "Toplu Mail" ekranını aç/kapat.</p></div>
        <Switch checked={!!form.bulk_mail_enabled} onCheckedChange={(v) => setForm({ ...form, bulk_mail_enabled: v })} data-testid="edit-bulk-switch" />
      </div>
      {form.bulk_mail_enabled && (
        <div className="grid grid-cols-2 gap-4">
          <Field label="Günlük limit" hint="24 saatte en fazla alıcı"><Input type="number" min={1} value={form.bulk_mail_daily_limit ?? 500} onChange={(e) => setForm({ ...form, bulk_mail_daily_limit: parseInt(e.target.value) || 0 })} /></Field>
          <Field label="Saatlik limit" hint="Kullanıcının seçebileceği en yüksek hız"><Input type="number" min={1} value={form.bulk_mail_hourly_limit ?? 200} onChange={(e) => setForm({ ...form, bulk_mail_hourly_limit: parseInt(e.target.value) || 0 })} /></Field>
        </div>
      )}
    </div>
  </Shell>
);

export const AddUserDialog = ({ open, onOpenChange, form, setForm, onSubmit, submitting }) => (
  <Shell open={open} onOpenChange={onOpenChange} title="Yeni kullanıcı ekle" description="Hesap cPanel'de zaten varsa webmail kaydını elle oluşturur." submitLabel="Oluştur" onSubmit={onSubmit} submitting={submitting} wide testId="add-user-dialog">
    <Field label="E-posta *"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="kisi@domain.com" autoFocus data-testid="new-user-email" /></Field>
    <Field label="Görünen ad"><Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Ad Soyad (opsiyonel)" /></Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Giriş şifresi *"><Input type="password" value={form.login_password} onChange={(e) => setForm({ ...form, login_password: e.target.value })} placeholder="Webmail girişi (en az 8)" autoComplete="new-password" data-testid="new-user-login-password" /></Field>
      <Field label="Posta kutusu şifresi *"><Input type="password" value={form.email_password} onChange={(e) => setForm({ ...form, email_password: e.target.value })} placeholder="cPanel posta şifresi" autoComplete="new-password" data-testid="new-user-mail-password" /></Field>
    </div>
    <div className="grid grid-cols-3 gap-3">
      <div className="col-span-2"><Field label="IMAP sunucu"><Input value={form.imap_host} onChange={(e) => setForm({ ...form, imap_host: e.target.value })} /></Field></div>
      <Field label="Port"><Input type="number" value={form.imap_port} onChange={(e) => setForm({ ...form, imap_port: e.target.value })} /></Field>
      <div className="col-span-2"><Field label="SMTP sunucu"><Input value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} /></Field></div>
      <Field label="Port"><Input type="number" value={form.smtp_port} onChange={(e) => setForm({ ...form, smtp_port: e.target.value })} /></Field>
    </div>
  </Shell>
);

export const AddAdminDialog = ({ open, onOpenChange, form, setForm, onSubmit, submitting }) => (
  <Shell open={open} onOpenChange={onOpenChange} title="Yeni admin ekle" submitLabel="Oluştur" onSubmit={onSubmit} submitting={submitting} testId="add-admin-dialog">
    <Field label="Kullanıcı adı"><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="admin_kullanici" autoFocus /></Field>
    <Field label="Şifre"><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="En az 6 karakter" autoComplete="new-password" /></Field>
    <Field label="Rol">
      <select className={`${selectClass} w-full`} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
        <option value="admin">Admin</option>
        <option value="super_admin">Süper admin</option>
      </select>
      <p className="text-xs text-muted-foreground">Süper admin: diğer adminleri yönetebilir.</p>
    </Field>
  </Shell>
);
